import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  format,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  startOfYear,
  endOfYear,
  subYears,
} from "date-fns";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export type FilterKey =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "last90"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "thisQuarter"
  | "lastQuarter"
  | "thisYear"
  | "lastYear"
  | "allTime";

interface DateFilterLinksProps {
  defaultKey?: FilterKey;
}

const filterMap: Record<FilterKey, () => { after: Date; before: Date } | null> =
  {
    today: () => ({
      after: startOfDay(new Date()),
      before: endOfDay(new Date()),
    }),
    yesterday: () => {
      const d = subDays(new Date(), 1);
      return { after: startOfDay(d), before: endOfDay(d) };
    },
    last7: () => ({
      after: startOfDay(subDays(new Date(), 6)),
      before: endOfDay(new Date()),
    }),
    last30: () => ({
      after: startOfDay(subDays(new Date(), 29)),
      before: endOfDay(new Date()),
    }),
    last90: () => ({
      after: startOfDay(subDays(new Date(), 89)),
      before: endOfDay(new Date()),
    }),
    thisWeek: () => ({
      after: startOfWeek(new Date(), { weekStartsOn: 1 }),
      before: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
    lastWeek: () => {
      const last = subWeeks(new Date(), 1);
      return {
        after: startOfWeek(last, { weekStartsOn: 1 }),
        before: endOfWeek(last, { weekStartsOn: 1 }),
      };
    },
    thisMonth: () => ({
      after: startOfMonth(new Date()),
      before: endOfMonth(new Date()),
    }),
    lastMonth: () => {
      const last = subMonths(new Date(), 1);
      return { after: startOfMonth(last), before: endOfMonth(last) };
    },
    thisQuarter: () => ({
      after: startOfQuarter(new Date()),
      before: endOfQuarter(new Date()),
    }),
    lastQuarter: () => {
      const last = subQuarters(new Date(), 1);
      return { after: startOfQuarter(last), before: endOfQuarter(last) };
    },
    thisYear: () => ({
      after: startOfYear(new Date()),
      before: endOfYear(new Date()),
    }),
    lastYear: () => {
      const last = subYears(new Date(), 1);
      return { after: startOfYear(last), before: endOfYear(last) };
    },
    allTime: () => null,
  };

const options: { key: FilterKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "last90", label: "Last 90 Days" },
  { key: "thisWeek", label: "This Week" },
  { key: "lastWeek", label: "Last Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "thisQuarter", label: "This Quarter" },
  { key: "lastQuarter", label: "Last Quarter" },
  { key: "thisYear", label: "This Year" },
  { key: "lastYear", label: "Last Year" },
  { key: "allTime", label: "All Time" },
];

const DateFilterLinks: React.FC<DateFilterLinksProps> = ({
  defaultKey = "last7",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState<FilterKey>(defaultKey);

  useEffect(() => {
    applyFilter(defaultKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKey]);

  function applyFilter(key: FilterKey) {
    setActiveKey(key);
    const range = filterMap[key]?.();
    const params = new URLSearchParams(location.search);

    if (range) {
      params.set("after", format(range.after, "yyyy-MM-dd"));
      params.set("before", format(range.before, "yyyy-MM-dd"));
    } else {
      params.delete("after");
      params.delete("before");
    }

    params.set("dateRangeFilter", key);
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  }

  return (
    <Select
      value={activeKey}
      onValueChange={(val) => applyFilter(val as FilterKey)}
    >
      <SelectTrigger className="w-[180px]">
        <SelectValue placeholder="Select Range" />
      </SelectTrigger>
      <SelectContent>
        {options.map(({ key, label }) => (
          <SelectItem key={key} value={key}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default DateFilterLinks;
